import Button from "components/atoms/buttons/Button";
import { CloseIcon, LeftArrowIcon, PlusIcon, PollIcon } from "components/atoms/Icon";
import MagnetImageContainer from "components/atoms/images/MagnetImageContainer";
import { useRouter } from "components/hooks";
import InputFieldWithLabel from "components/molecules/forms/InputFieldWithLabel";
import { useState } from "react";
import { useFieldArray, useFormContext, UseFormHandleSubmit } from "react-hook-form";
import DatePickerInput from "components/atoms/forms/datepicker/DatePickerInput";
import DefaultImage from "../../../assets/Images/BrandImage1.png";
import PollSelectElement from "./PollSelectElement";
import PollExitConfirmationModal from "./PollExitConfirmationModal";
import PollQuestion from "./PollQuestion";

type Props = {
  handlePublishPoll: (formData: PollFormDataNew) => void;
  handleSaveAsDraftOrUpdatePoll: (formData: PollFormDataNew) => void;
  handleDeletePoll: () => void;
  handleSubmit: UseFormHandleSubmit<PollFormDataNew>;
  magnetName: string;
  magnetImageUrl: string;
  brandName: string;
  isEditPollForm?: boolean;
  isPollPublished: boolean;
  refetchPollData: () => void;
};

const pollVisibilityOptions = [
  {
    key: "Everyone",
    value: 0,
  },
  {
    key: "Magnet Owner Only",
    value: 1,
  },
];

function PollForm({
  handlePublishPoll,
  handleSaveAsDraftOrUpdatePoll,
  handleDeletePoll,
  handleSubmit,
  magnetName,
  magnetImageUrl,
  brandName,
  isEditPollForm,
  isPollPublished,
  refetchPollData,
}: Props) {
  const [isEndDatePickerModalOpen, setIsEndDatePickerModalOpen] = useState(false);
  const [isStartDatePickerModalOpen, setIsStartDatePickerModalOpen] = useState(false);

  const router = useRouter();

  const {
    setValue,
    register,
    formState: { errors, isDirty },
    control,
    watch,
  } = useFormContext<PollFormDataNew>();

  const {
    fields: questions,
    append: appendQuestion,
    remove: removeQuestion,
  } = useFieldArray({ control, name: "questions" });

  const handleAddQuestion = () => {
    appendQuestion({ question: "", choices: [{ text: "" }, { text: "" }] });
  };

  const handleSetEndDateTime = (value: any) => {
    setValue("end_time", value);
  };
  const handleSetStartDateTime = (value: any) => {
    setValue("start_time", value);
  };
  const handleRemoveQuestion = (idx: number) => {
    removeQuestion(idx);
  };

  const [exitConfirmationModalOpen, setExitConfirmationModalOpen] = useState(false);

  const onExitModalClose = () => {
    setExitConfirmationModalOpen(false);
  };

  const navigateToPreviousPage = () => {
    router.go(-1);
  };

  const handleBackButtonClick = () => {
    if (isDirty) setExitConfirmationModalOpen(true);
    else navigateToPreviousPage();
  };

  const handleExitModalSaveAsDraftButtonClick = () => {
    setExitConfirmationModalOpen(false);
    handleSubmit(handleSaveAsDraftOrUpdatePoll)();
  };

  refetchPollData();
  return (
    <>
      {exitConfirmationModalOpen && (
        <PollExitConfirmationModal
          onModalClose={onExitModalClose}
          handleCancelButtonClick={navigateToPreviousPage}
          handleSaveAsDraftButtonClick={handleExitModalSaveAsDraftButtonClick}
        />
      )}
      <div className="mt-8 font-sora 2xl:mt-12">
        <div className="main-container min-h-[80vh]">
          <div className="flex h-full">
            <div>
              <button type="button" className="" onClick={handleBackButtonClick}>
                <LeftArrowIcon className="h-4 w-6 2xl:h-6 2xl:w-9" />
              </button>
            </div>
            <div className="ml-3 h-full grow space-y-2 2xl:ml-4 2xl:space-y-4">
              <div className="flex space-x-3 2xl:space-x-5">
                <div className="h-20 w-20 rounded-xl border border-white 2xl:h-24 2xl:w-24">
                  <MagnetImageContainer image={magnetImageUrl || DefaultImage} altText={magnetName} />
                </div>
                <div className="mt-1">
                  <div className="flex space-x-1 2xl:space-x-2">
                    <PollIcon className="h-4 w-4 2xl:h-5 2xl:w-5" />
                    <p className="font-satoshi text-xs font-semibold leading-5 text-white 2xl:text-sm">
                      Community Poll
                    </p>
                  </div>
                  <p className="mt-0 font-satoshi text-2xl font-bold leading-10 text-white 2xl:mt-2 2xl:text-3xl">
                    {magnetName}
                  </p>
                  <p className="font-satoshi text-xxs font-semibold leading-5 text-white text-opacity-70 2xl:text-xs">
                    {brandName}
                  </p>
                </div>
              </div>
              <p className="font-satoshi text-xs font-450 leading-6 text-secondary 2xl:text-sm">
                Fill out the form to create a poll for the Magnet owners.
              </p>
              <div className="grid grid-cols-12">
                <div className="secondary-scrollbar col-span-4 -mr-10 h-[70vh] overflow-y-auto pr-12">
                  <InputFieldWithLabel
                    label="Poll Name"
                    id="name"
                    isDisabled={isPollPublished}
                    additionalClasses="text-xs 2xl:text-sm"
                    labelClasses="text-xxs 2xl:text-xs"
                    register={register}
                    placeHolder="Enter the name of the poll"
                    errorMessage={errors?.name?.message}
                  />
                  {questions.map((question, qID) => (
                    <div key={question.id} className="relative">
                      <button
                        disabled={qID < 1 || isPollPublished}
                        type="button"
                        title="remove question"
                        className="absolute right-2 top-2 z-20 disabled:hidden"
                        onClick={() => handleRemoveQuestion(qID)}
                      >
                        <CloseIcon className="h-3 w-3 text-red-600" />
                      </button>
                      <PollQuestion isPollPublished={isPollPublished} qId={qID} />
                    </div>
                  ))}
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      // user is not allowed to add options if poll is published
                      disabled={isPollPublished}
                      className="flex items-center gap-2 rounded-full bg-secondary p-3 text-white disabled:hidden 2xl:p-4"
                      // @ts-ignore
                      onClick={handleAddQuestion}
                    >
                      <PlusIcon className="h-3 w-3 2xl:h-4 2xl:w-4" /> Add New Question
                    </button>
                  </div>
                </div>
                <div className="col-span-3 col-start-7 flex flex-col justify-between">
                  <div>
                    <p className=" font-sora text-xs font-medium text-white 2xl:text-sm">Settings</p>
                    <div className="mt-2 w-2/3">
                      <DatePickerInput
                        label="Start Time"
                        isDatePickerModalOpen={isStartDatePickerModalOpen}
                        setIsDatePickerModalOpen={setIsStartDatePickerModalOpen}
                        startDateTime={watch("start_time")}
                        error={errors.start_time?.message}
                        handleSetDateTimeForMomemto={handleSetStartDateTime}
                        minDate={new Date()}
                        id="start_time"
                      />
                    </div>
                    <div className="mt-3 w-2/3">
                      <DatePickerInput
                        label="Accept Responses Until"
                        isDatePickerModalOpen={isEndDatePickerModalOpen}
                        setIsDatePickerModalOpen={setIsEndDatePickerModalOpen}
                        startDateTime={watch("end_time")}
                        error={errors.end_time?.message}
                        handleSetDateTimeForMomemto={handleSetEndDateTime}
                        minDate={new Date()}
                        id="end_time"
                      />
                    </div>
                    <div className="mt-3 2xl:mt-5">
                      <PollSelectElement
                        isDisabled={isPollPublished}
                        label="Who Can View"
                        register={register}
                        labelClasses="text-xxs 2xl:text-xs"
                        additionalClasses="text-xs 2xl:text-sm rounded-ll"
                        options={pollVisibilityOptions}
                        id="viewer_type"
                        errorMessage={errors?.viewer_type?.message}
                      />
                    </div>
                  </div>
                  {!isPollPublished && (
                    <div className="flex w-full justify-end gap-3 pb-6">
                      {isEditPollForm && (
                        <Button
                          variant="whiteOutline"
                          text="DELETE"
                          additionalClasses="xl:px-6 xl:py-3 xl:text-xs 2xl:text-sm 2xl:px-8 2xl:py-4"
                          onClick={handleDeletePoll}
                        />
                      )}

                      <Button
                        variant="rustPinkSolid"
                        text={isEditPollForm ? "UPDATE" : "SAVE AS DRAFT"}
                        additionalClasses="xl:px-6 xl:py-3 xl:text-xs 2xl:text-sm 2xl:px-8 2xl:py-4"
                        onClick={handleSubmit(handleSaveAsDraftOrUpdatePoll)}
                      />

                      <Button
                        text="PUBLISH"
                        additionalClasses="xl:px-6 xl:py-3 xl:text-xs 2xl:text-sm 2xl:px-8 2xl:py-4"
                        onClick={handleSubmit(handlePublishPoll)}
                        variant="magnetiqSolid"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default PollForm;
